import { Button } from '../../components/Button';
import { ErrorText } from '../../components/ErrorText';
import { InfoNote } from '../../components/InfoNote';
import { RadioGroup } from '../../components/RadioGroup';
import { SectionHeader } from '../../components/SectionHeader';
import { SkeletonBlock } from '../../components/SkeletonBlock';
import { StickyActionBar } from '../../components/StickyActionBar';
import { formatCurrency } from '../../utils/formatCurrency';
import { ActionBar } from '../ActionBar';
import { GuestDetailsPanel } from '../GuestDetailsPanel';
import { PackageInclusionChoicePanel } from '../PackageInclusionChoicePanel';
import { PackageInclusionsPanel } from '../PackageInclusionsPanel';
import { PreferencesPanel } from '../PreferencesPanel';
import {
  ActionBarArea,
  CardSection,
  FooterLeft,
  FooterRow,
  FooterTitle,
  FooterTotal,
  FormGroup,
  GuestModeOptions,
  Heading,
  HeadingAccent,
  Inner,
  OutlinedCard,
  PageError,
  PreferencesCardBody,
  ScrollArea,
  Wrapper,
} from './PackageExperiencesContentPanel.styles';
import type {
  PackageExperiencesContentPanelProps,
  PackageExperiencesGuestMode,
} from './PackageExperiencesContentPanel.types';

export function PackageExperiencesContentPanel({
  sections,
  guestMode,
  onGuestModeChange,
  showGuestModeChoice = true,
  isGroupModeUnavailable = false,
  errorMessage,
  isGuestSelectionReadOnly = false,
  onExistingGuestSelect,
  onGuestChange,
  onDayChange,
  onTimeChange,
  onChoiceToggle,
  onChoiceDayChange,
  onChoiceTimeChange,
  preferences = [],
  onPreferenceOptionToggle,
  onPreferenceOtherChange,
  onPreferenceFreeTextChange,
  onPreferenceExpandedChange,
  onBack,
  currentStep = 1,
  totalSteps,
  footerTitle,
  totalAmount,
  currencyCode,
  locale = 'en-GB',
  onNext,
  nextDisabled = false,
  isLoading = false,
  labels = {},
}: PackageExperiencesContentPanelProps) {
  const {
    title = 'Experiences.',
    titleAccent = 'Tailor your visit, just the way you like it.',
    backAriaLabel = 'Go back',
    progressLabel = 'Booking progress',
    guestModeTitle = 'Guests.',
    guestModeSubtitle = 'Choose experiences as a group or separately',
    guestModeGroupLabel = 'Choose as group',
    guestModeSeparateLabel = 'Choose separately',
    guestModeDisabledNote,
    guestTitleFormatter,
    guestSubtitle,
    inclusions: inclusionLabels,
    choice: choiceLabels,
    otherButtonLabel,
    totalLabel = 'Total',
    nextLabel = 'Next',
  } = labels;

  const selectedGuestIds = sections
    .map((section) => section.guest?.selectedGuestId)
    .filter((id): id is string => id !== undefined);

  return (
    <Wrapper data-testid="package-experiences-content-panel" aria-busy={isLoading || undefined}>
      <ScrollArea>
        <Inner>
          <ActionBar
            onBack={onBack}
            backAriaLabel={backAriaLabel}
            currentStep={currentStep}
            totalSteps={totalSteps}
            progressLabel={progressLabel}
          />
          {isLoading ? (
            <>
              <Heading>
                <SkeletonBlock variant="heading" />
              </Heading>
              <FormGroup>
                <SkeletonBlock height="220px" borderRadius="24px" />
                <SkeletonBlock height="220px" borderRadius="24px" />
              </FormGroup>
            </>
          ) : (
            <>
              <Heading>
                {title} <HeadingAccent>{titleAccent}</HeadingAccent>
              </Heading>
              <FormGroup>
                {errorMessage && (
                  <PageError role="alert">
                    <ErrorText>{errorMessage}</ErrorText>
                  </PageError>
                )}
                {showGuestModeChoice && (
                  <OutlinedCard>
                    <CardSection>
                      <SectionHeader title={guestModeTitle} subtitle={guestModeSubtitle} />
                      <GuestModeOptions>
                        <RadioGroup
                          name="package-experiences-guest-mode"
                          options={[
                            {
                              value: 'group',
                              label: guestModeGroupLabel,
                              disabled: isGroupModeUnavailable,
                            },
                            { value: 'separate', label: guestModeSeparateLabel },
                          ]}
                          value={guestMode}
                          direction="column"
                          variant="card"
                          onChange={(value) =>
                            onGuestModeChange(value as PackageExperiencesGuestMode)
                          }
                        />
                        {isGroupModeUnavailable && guestModeDisabledNote && (
                          <InfoNote text={guestModeDisabledNote} />
                        )}
                      </GuestModeOptions>
                    </CardSection>
                  </OutlinedCard>
                )}

                {sections.map((section) => (
                  <OutlinedCard key={section.id}>
                    {section.guest && (
                      <CardSection>
                        <GuestDetailsPanel
                          guestNumber={section.guest.guestNumber}
                          subtitle={guestSubtitle}
                          existingGuests={section.guest.existingGuests}
                          selectedGuestId={section.guest.selectedGuestId}
                          disabledGuestIds={selectedGuestIds.filter(
                            (id) => id !== section.guest?.selectedGuestId
                          )}
                          onExistingGuestSelect={(guestId) =>
                            onExistingGuestSelect(section.id, guestId)
                          }
                          values={section.guest.values}
                          onChange={(field, value) => onGuestChange(section.id, field, value)}
                          showManualForm={false}
                          isSelectionReadOnly={isGuestSelectionReadOnly}
                          labels={{ guestTitleFormatter }}
                        />
                      </CardSection>
                    )}

                    {section.inclusions.length > 0 && (
                      <CardSection>
                        <PackageInclusionsPanel
                          inclusions={section.inclusions}
                          onDayChange={(inclusionId, value) =>
                            onDayChange(section.id, inclusionId, value)
                          }
                          onTimeChange={(inclusionId, value) =>
                            onTimeChange(section.id, inclusionId, value)
                          }
                          labels={inclusionLabels}
                        />
                      </CardSection>
                    )}

                    {section.choiceGroups.map((group) => (
                      <CardSection key={group.id}>
                        <PackageInclusionChoicePanel
                          options={group.options}
                          selectedOptionIds={group.selectedOptionIds}
                          maximumSelections={group.maximumSelections}
                          selectionCountText={group.selectionCountText}
                          optionError={group.optionError}
                          onOptionToggle={(optionId) =>
                            onChoiceToggle(section.id, group.id, optionId)
                          }
                          onDayChange={(optionId, value) =>
                            onChoiceDayChange(section.id, group.id, optionId, value)
                          }
                          onTimeChange={(optionId, value) =>
                            onChoiceTimeChange(section.id, group.id, optionId, value)
                          }
                          labels={{
                            ...choiceLabels,
                            title: group.title ?? choiceLabels?.title,
                            subtitle: group.subtitle ?? choiceLabels?.subtitle,
                          }}
                        />
                      </CardSection>
                    ))}
                  </OutlinedCard>
                ))}

                {preferences.length > 0 && (
                  <OutlinedCard>
                    <PreferencesCardBody>
                      <PreferencesPanel
                        categories={preferences}
                        onOptionToggle={(categoryId, option) =>
                          onPreferenceOptionToggle?.(categoryId, option)
                        }
                        onOtherChange={(categoryId, value) =>
                          onPreferenceOtherChange?.(categoryId, value)
                        }
                        onFreeTextChange={(categoryId, value) =>
                          onPreferenceFreeTextChange?.(categoryId, value)
                        }
                        onExpandedChange={onPreferenceExpandedChange}
                        otherButtonLabel={otherButtonLabel}
                      />
                    </PreferencesCardBody>
                  </OutlinedCard>
                )}
              </FormGroup>
            </>
          )}
        </Inner>
      </ScrollArea>
      <ActionBarArea>
        <StickyActionBar>
          <FooterRow>
            <FooterLeft>
              {isLoading ? (
                <>
                  <SkeletonBlock width="140px" height="16px" />
                  <SkeletonBlock width="90px" height="20px" />
                </>
              ) : (
                <>
                  {footerTitle && <FooterTitle>{footerTitle}</FooterTitle>}
                  <FooterTotal>
                    {totalLabel} {formatCurrency(totalAmount, currencyCode, { locale })}
                  </FooterTotal>
                </>
              )}
            </FooterLeft>
            {isLoading ? (
              <SkeletonBlock width="96px" height="48px" borderRadius="24px" />
            ) : (
              <Button type="button" variant="primary" onClick={onNext} disabled={nextDisabled}>
                {nextLabel}
              </Button>
            )}
          </FooterRow>
        </StickyActionBar>
      </ActionBarArea>
    </Wrapper>
  );
}
